import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

export default function RenderProAPI() {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);

  const lastGridChangeTime = useRef(0);
  const bgIntervalRef = useRef(null);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  useEffect(() => {
    document.title = "RenderPro API | Outlne";
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1224);
      setWindowWidth(width);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Background slideshow
  useEffect(() => {
    if (!pageLoaded) return;

    backgrounds.forEach(src => {
      const img = new Image();
      img.src = src;
    });

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';

      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);

      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    changeBackground();
    bgIntervalRef.current = setInterval(changeBackground, 8000);

    return () => clearInterval(bgIntervalRef.current);
  }, [pageLoaded]);

  // Grid animation
  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;

      const timeSinceChange = time - lastGridChangeTime.current;

      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }

      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid(prev => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Background Slideshow */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
        }} />
      </div>

      {/* Grid Container */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, white 1px, transparent 1px),
                linear-gradient(to bottom, white 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* Desktop Header */}
      {!isMobile && (
        <>


          <div style={{
            position: 'fixed',
            top: '40px',
            right: '40px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img
                src={LogoWhite}
                alt="Outlne Logo"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <h1 style={{
                fontSize: '1.8rem',
                fontFamily: 'avenir-lt-pro, sans-serif',
                letterSpacing: '-1.2px',
                margin: 0,
                color: 'white',
                fontWeight: 500,
              }}>
                Outlne
              </h1>
            </div>
            <p style={{
              fontSize: '1rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design{' '}
              <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>
        </>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '110px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 15,
          }} />

          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 20,
            textAlign: 'left',
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              letterSpacing: '-1.2px',
              margin: 0,
              color: 'white',
              fontWeight: 500,
            }}>
              Outlne
            </h1>
            <p style={{
              fontSize: '0.9rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design{' '}
              <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>

          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img
              src={LogoWhite}
              alt="Outlne Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />

          </div>
        </>
      )}

      {/* Main Content – UPDATED SECTION */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 5,
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 1s ease',
        overscrollBehavior: 'none'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '100vh',
          paddingTop: isMobile ? '135px' : isTablet ? '80px' : '80px',
          paddingBottom: '0',
          position: 'relative',
          zIndex: 5,
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: isMobile ? '100%' : isTablet ? '600px' : '500px',
            padding: isMobile ? '0 20px' : '0 40px',
            flex: '1',
            textAlign: 'center',
            color: 'white',
          }}>

            {/* --- Image Container (Moved to top, above header) --- */}
            <div style={{
              width: isMobile ? '280px' : '260px',
              margin: '0 auto 2rem auto',
              border: '2px solid white',
              borderRadius: '32px',
              padding: '12px',
              boxSizing: 'border-box',
              backgroundColor: 'transparent',
            }}>
              <img
                src="https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/app%2Fassets%2Frenderproapi_preview1.JPG?alt=media&token=ac298ef4-36d4-47fc-bab7-323704805c8d"
                alt="RenderPro preview"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: '20px',
                }}
              />
            </div>

            {/* Header */}
            <h1 style={{
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontSize: isMobile ? '1.6rem' : isTablet ? '2rem' : '2.2rem',
              fontWeight: '500',
              marginBottom: '1.2rem',
              lineHeight: '1.2',
              letterSpacing: '-0.5px',
            }}>
              Introducing RenderPro API
            </h1>

            {/* Description paragraph */}
            <p style={{
              color: '#ccc',
              fontSize: isMobile ? '0.95rem' : '1.1rem',
              lineHeight: '1.6',
              fontFamily: 'avenir-lt-pro, sans-serif',
              maxWidth: '450px',
              margin: '0 auto 2rem auto',
            }}>
              RenderPro API is our proprietary prompt engineering system built to help users achieve<br></br> ultra-realistic image generation designed specifically for developers in the Interior Design and Architecture community space.
            </p>

            {/* Contact email line */}
            <p style={{
              color: '#ccc',
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              marginBottom: '2rem',
            }}>
              To schedule a meeting contact us at{' '}
              <a href="mailto:developers@outlne.com" style={{ color: '#ffffffff', textDecoration: 'underline' }}>
                developers@outlne.com
              </a>
            </p>

            {/* Blue Contact button */}
            <a
              href="mailto:developers@outlne.com"
              style={{
                backgroundColor: '#419affff',
                color: '#ffffff',
                padding: '14px 40px',
                borderRadius: '12px',
                textDecoration: 'none',
                fontFamily: 'avenir-lt-pro, sans-serif',
                fontSize: '1.1rem',
                fontWeight: '600',
                display: 'inline-block',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 14px rgba(104, 168, 255, 0.4)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#89bfff';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(104, 168, 255, 0.6)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#68a8ff';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(104, 168, 255, 0.4)';
              }}
            >
              Contact
            </a>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          width: '100%',
          position: 'relative',
          zIndex: 5,
          backgroundColor: '#1a1a1a',
          marginTop: isMobile ? '3rem' : '4rem',
        }}>
          <Footer />
        </div>
      </div>

      <style>{`
        html, body, #root {
          overflow: hidden;
          height: 100%;
          width: 100%;
          position: fixed;
          margin: 0;
          padding: 0;
        }
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
    </>
  );
}